import React, { useState, useEffect } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";

const StripePortalLinkModal = ({ isOpen, onClose }) => {
  const [chemists, setChemists] = useState([]);
  const [isFetchingChemists, setIsFetchingChemists] = useState(false);
  const [selectedChemist, setSelectedChemist] = useState(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchChemists();
    } else {
      setSelectedChemist(null);
      setGeneratedUrl(null);
      setError(null);
      setCopied(false);
    }
  }, [isOpen]);

  const fetchChemists = async () => {
    setIsFetchingChemists(true);
    try {
      const response = await phappsRequest(API_PATHS.CHEMIST_LIST, "GET");
      if (response.success && Array.isArray(response.chemists)) {
        setChemists(response.chemists);
      } else {
        setError("Failed to load chemists list");
      }
    } catch {
      setError("Error loading chemists");
    } finally {
      setIsFetchingChemists(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedChemist) {
      setError("Please select a chemist first");
      return;
    }
    setIsGenerating(true);
    setGeneratedUrl(null);
    setError(null);
    setCopied(false);

    try {
      const response = await phappsRequest(API_PATHS.STRIPE_PORTAL_LINK, "POST", {
        chemistID: selectedChemist.chemistID,
      });
      if (response.success && response.url) {
        setGeneratedUrl(response.url);
      } else {
        setError(response.error || "Failed to generate link");
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    await navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-violet-600 fill-current">
              <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900">Get Stripe chemist portal link</h2>
        </div>

        <p className="text-sm text-gray-500 mb-5">
          Select a pharmacy to generate a secure Stripe customer portal link. The link expires in 30 days.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pharmacy</label>
            <Autocomplete
              options={chemists}
              getOptionLabel={(option) => `${option.chemistName} (ID: ${option.chemistID})`}
              value={selectedChemist}
              onChange={(_, newValue) => {
                setSelectedChemist(newValue);
                setGeneratedUrl(null);
                setError(null);
              }}
              loading={isFetchingChemists}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search pharmacy..."
                  variant="outlined"
                  size="small"
                />
              )}
              style={{ width: "100%" }}
            />
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
          )}

          {generatedUrl && (
            <div className="p-3 rounded-lg bg-green-50 border border-green-200">
              <p className="text-xs font-medium text-green-700 mb-1.5">Portal link generated</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-800 font-mono break-all flex-1">{generatedUrl}</span>
                <button
                  onClick={handleCopy}
                  title="Copy to clipboard"
                  className="flex-shrink-0 p-1.5 rounded-md text-green-700 hover:bg-green-100 transition-colors"
                >
                  {copied ? (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating || !selectedChemist}
            className="px-4 py-2 text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 text-sm font-medium"
          >
            {isGenerating ? "Generating..." : "Generate link"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripePortalLinkModal;
